<template>
  <section class="city-analytics" :class="{ 'dashboard-redesign': dashboardRedesignEnabled }" aria-label="Municipal analytics">
    <header class="analytics-heading">
      <div>
        <h1>Overview</h1>
        <p v-if="snapshot">Snapshot from <time :datetime="snapshot.generatedAt">{{ fmtDate(snapshot.generatedAt, { withTime: true }) }}</time></p>
      </div>
      <button type="button" class="reload-button" :disabled="loading" @click="load(true)"><i class="bi-arrow-clockwise" aria-hidden="true" /> Reload snapshot</button>
    </header>

    <DashboardAssistant v-if="ENABLE_DASHBOARD_ASSISTANT" :city-id="cityId" :city-name="cityName" :snapshot="snapshot" />

    <p v-if="loading" role="status">Loading town analytics…</p>
    <div v-else-if="error" :role="unavailable ? 'status' : 'alert'" class="analytics-notice">
      {{ error }} <span v-if="unavailable">You can still use the management links below.</span>
    </div>
    <p v-else-if="snapshot" class="snapshot-note">
      Reloading checks for the latest saved snapshot.
      <strong v-if="stale">This snapshot is more than four days old.</strong>
    </p>

    <section v-if="showSnapshot || !dashboardRedesignEnabled" aria-label="Where the town stands">
      <h2 class="visually-hidden">Where the town stands</h2>
      <div class="analytics-grid overview-grid" :class="{ 'overview-summary-only': !showSnapshot }">
        <template v-if="showSnapshot">
          <ComplianceRateCard :key="`compliance-${cityId}`" v-model="complianceOptions" :city-id="cityId" :data="snapshot.propertyCompliance" :redesign="dashboardRedesignEnabled" :generated-at="snapshot.generatedAt" :snapshot-version="snapshot.snapshotVersion" :history-rows="historyRows" :history-loading="historyLoading" :history-error="historyError" @retry-history="reloadHistory" @view-trends="openTrends('complianceTrends')" />
          <article class="analytics-card active-listings-card">
            <div class="card-heading"><h3 class="card-label">Active listings</h3><button type="button" class="view-trends" aria-label="View listing trends" title="View listing trends" aria-haspopup="dialog" @click="openTrends('listingTrends')"><i class="bi-graph-up" aria-hidden="true" /></button></div>
            <template v-if="!dashboardRedesignEnabled">
              <div class="classic-card-content">
                <div class="classic-card-summary">
                  <div class="stat-value active-listing-total">{{ fmtInt(snapshot.inventory.activeListings) }}</div>
                  <DashboardTrend metric="activeListings" :value="snapshot.inventory.activeListings" :generated-at="snapshot.generatedAt" :snapshot-version="snapshot.snapshotVersion" :rows="historyRows" :loading="historyLoading" :error="historyError" @retry="reloadHistory" />
                </div>
                <div class="classic-card-breakdown">
                  <p class="verification-heading verification-rate"><strong>{{ displayPercent(verifiedShare) }}</strong> with a verified property / address</p>
                  <template v-if="listingPopulation">
                    <DashboardGraphic v-if="listingPopulation.total" kind="stack" :label="listingPopulationLabel" :options="classicListingChart" />
                    <div v-if="listingPopulation.deferred" class="population-labels">
                      <div v-if="listingPopulation.included"><strong>{{ fmtInt(listingPopulation.included) }}</strong> included<span>Used in verification rate</span></div>
                      <div v-if="listingPopulation.deferred" class="deferred-context"><strong>{{ fmtInt(listingPopulation.deferred) }}</strong> deferred<span>Excluded from rate</span></div>
                    </div>
                    <ul v-if="listingPopulation.total" class="legend listing-counts" aria-label="Listing verification counts">
                      <li v-for="item in classicListingChart.segments" :key="item.key">
                        <span class="legend-dot" :style="{ background: item.color }" />
                        {{ item.label }} <strong>{{ fmtInt(item.value) }}</strong>
                      </li>
                    </ul>
                    <p v-if="!listingPopulation.included" class="fine-print">{{ listingPopulation.total ? 'No non-deferred active listings.' : 'No active listings to verify.' }}</p>
                  </template>
                  <p v-else class="fine-print">Verification and deferral counts are not available in this snapshot.</p>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="classic-card-content">
                <div class="classic-card-summary">
                  <div class="stat-value active-listing-total">{{ fmtInt(snapshot.inventory.activeListings) }}</div>
                  <DashboardTrend metric="activeListings" :value="snapshot.inventory.activeListings" :generated-at="snapshot.generatedAt" :snapshot-version="snapshot.snapshotVersion" :rows="historyRows" :loading="historyLoading" :error="historyError" @retry="reloadHistory" />
                </div>
                <div class="classic-card-breakdown">
                  <div class="listing-verification">
                    <template v-if="verificationChart">
                      <div class="donut-breakdown">
                        <div class="verification-donut">
                          <DashboardGraphic kind="donut" :label="`Verification rate: ${displayPercent(verifiedShare)}. ${fmtInt(listingPopulation.verified)} verified out of ${fmtInt(listingPopulation.included)} non-deferred active listings. Deferred listings are excluded.`" :options="verificationChart" />
                          <div class="stat-value verification-rate" aria-hidden="true">{{ displayPercent(verifiedShare) }}</div>
                        </div>
                        <div class="verification-summary">
                          <ul v-if="listingPopulation.included" class="legend verification-legend">
                            <li v-for="item in verificationChart.segments" :key="item.key">
                              <span class="legend-dot" :style="{ background: item.color }" />
                              {{ item.label }} <strong>{{ fmtInt(item.value) }}</strong>
                            </li>
                          </ul>
                          <p v-if="listingPopulation.deferred || !listingPopulation.included" class="fine-print">{{ listingPopulation.included ? `${fmtInt(listingPopulation.included)} listings in rate` : 'No non-deferred active listings.' }}</p>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <p class="stat-caption verification-rate"><strong>—</strong> verified</p>
                      <p class="fine-print">Verification and deferral counts are not available in this snapshot.</p>
                    </template>
                  </div>
                  <div v-if="listingPopulation" class="listing-population">
                    <div class="population-heading"><span>All active listings</span><strong>{{ fmtInt(listingPopulation.total) }} total</strong></div>
                    <template v-if="listingPopulation.total">
                      <DashboardGraphic kind="stack" :label="listingPopulationLabel" :options="listingPopulationChart" />
                      <div v-if="listingPopulation.deferred" class="population-labels">
                        <div v-if="listingPopulation.included"><strong>{{ fmtInt(listingPopulation.included) }}</strong> included<span>Shown in donut above</span></div>
                        <div v-if="listingPopulation.deferred" class="deferred-context"><strong>{{ fmtInt(listingPopulation.deferred) }}</strong> deferred<span>Excluded from rate</span></div>
                      </div>
                    </template>
                    <p v-else class="fine-print">No active listings to verify.</p>
                    <p class="fine-print verification-definition">Verified listings have a confirmed property / address.</p>
                  </div>
                </div>
              </div>
              <section v-if="listingDiscoveryHistory.length" class="listing-discovery" aria-label="Listing discovery history">
                <h4>Newly discovered listings</h4>
                <ol class="discovery-history">
                  <li v-for="period in listingDiscoveryHistory" :key="period.key" :class="{ partial: period.partial }">
                    <strong>{{ fmtInt(period.count) }}</strong>
                    <div class="discovery-track" aria-hidden="true"><span v-if="period.count > 0" class="discovery-bar" :style="{ height: period.height + '%' }" /></div>
                    <span class="discovery-period">{{ period.label }}</span>
                    <span class="discovery-partial">{{ period.partial ? 'to date' : '' }}</span>
                  </li>
                </ol>
                <p class="fine-print">First seen by fiscal quarter; includes listings now inactive.</p>
              </section>
            </template>
            <nav class="card-actions" aria-label="Listing management">
              <router-link :to="route('listings')">Listings <i class="bi-arrow-right" aria-hidden="true" /></router-link>
            </nav>
          </article>
          <article class="analytics-card housing-card">
            <h3 class="card-label">STR properties per housing unit</h3>
            <template v-if="has('housingShare')">
              <div class="stat-value">{{ fmtPct(snapshot.inventory.strPenetration) }}</div>
              <p>{{ fmtInt(snapshot.inventory.strProperties) }} listed or licensed STR properties / {{ fmtInt(snapshot.inventory.housingUnits) }} housing units.</p>
              <p class="fine-print">{{ snapshot.inventory.census?.vintage || 'US Census' }}. Property records and housing units are different measures; this is an approximate ratio.</p>
            </template>
            <p v-else class="unavailable">{{ reason('housingShare') }}</p>
          </article>
          <PlatformListingsCard :key="`platform-${cityId}`" :rows="snapshot.inventory.bySource" :total="snapshot.inventory.activeListings" @view-trends="openTrends('platformListingCount')" />
        </template>
        <section v-if="!dashboardRedesignEnabled" class="analytics-card license-activity" aria-label="License activity">
          <div class="section-heading"><h2>License activity</h2><router-link :to="route('licenses')">Manage licenses <i class="bi-arrow-right" aria-hidden="true" /></router-link></div>
          <p class="fine-print">License records · Current operational summary<span v-if="summary?.updatedAt"> · {{ fmtDate(summary.updatedAt, { withTime: true }) }}</span></p>
          <p v-if="summaryLoading" role="status">Loading license activity…</p>
          <p v-else-if="summaryError" role="alert">{{ summaryError }} <button type="button" class="reload-button" @click="$emit('reload-summary')">Retry</button></p>
          <div v-else-if="summary" class="license-stats">
            <router-link v-for="stat in licenseStats" :key="stat.label" :to="route('licenses', {}, stat.query || {})">
              <span>{{ stat.label }}</span><strong>{{ fmtInt(stat.value) }}</strong>
            </router-link>
          </div>
          <p v-else class="fine-print">License activity is not available yet.</p>
          <nav v-if="!snapshot" class="management-links" aria-label="Municipal management"><router-link :to="route('violations')">Manage violations</router-link><router-link :to="route('listings')">Manage listings</router-link></nav>
        </section>
      </div>
    </section>

    <section v-if="dashboardRedesignEnabled" class="analytics-card license-workflow" aria-label="License Workflow">
      <div class="section-heading"><h2>License Workflow</h2><router-link :to="route('licenses')">Manage licenses <i class="bi-arrow-right" aria-hidden="true" /></router-link></div>
      <p class="fine-print">Unique properties by their most relevant license<span v-if="snapshot?.generatedAt"> · {{ fmtDate(snapshot.generatedAt, { withTime: true }) }}</span></p>
      <p v-if="loading" role="status">Loading property license workflow…</p>
      <p v-else-if="error" role="alert">{{ error }} <button type="button" class="reload-button" @click="load">Retry</button></p>
      <template v-else-if="licenseWorkflowChart">
        <div v-if="licenseWorkflowTotal > 0" class="license-workflow-breakdown">
          <DashboardGraphic kind="donut" :label="`${fmtInt(licenseWorkflowTotal)} unique properties by license workflow status`" :options="licenseWorkflowChart" />
          <ul class="legend workflow-legend" aria-label="Property counts by license workflow status">
            <li v-for="item in licenseWorkflowChart.segments" :key="item.key">
              <span class="legend-dot" :style="{ background: item.color }" />
              <span>{{ item.label }}</span><strong>{{ fmtInt(item.value) }}</strong>
            </li>
          </ul>
        </div>
        <p v-else class="fine-print">No properties with linked licenses.</p>
        <p class="fine-print workflow-note">Each property is counted once: active license first, then pending, inactive, expired, or unknown; the newest record breaks ties.</p>
      </template>
      <p v-else class="fine-print">Property workflow counts are not available in this snapshot yet.</p>
      <p v-if="snapshot?.licenseWorkflow?.excludedLicenseRecords" class="fine-print">{{ fmtInt(snapshot.licenseWorkflow.excludedLicenseRecords) }} license records without a valid property are excluded.</p>
      <nav v-if="!snapshot" class="management-links" aria-label="Municipal management"><router-link :to="route('violations')">Manage violations</router-link><router-link :to="route('listings')">Manage listings</router-link></nav>
    </section>

    <template v-if="showSnapshot">
      <section aria-label="Enforcement queue">
        <h3>Enforcement queue</h3>
        <div v-if="!has('parcels')" class="analytics-notice">{{ reason('parcels') }}</div>
        <div v-else class="analytics-grid">
          <article class="analytics-card">
            <h4>Open violations by age</h4>
            <p><strong>{{ fmtInt(snapshot.violations.openCount) }} open</strong> · Median {{ fmtDays(snapshot.violations.medianOpenDays) }}</p>
            <DashboardGraphic kind="bar" label="Open violations by age" :options="agingChart" />
            <p class="fine-print">Includes {{ fmtInt(snapshot.queue?.deferredViolations || 0) }} deferred properties. Ages use the recorded transition into violation, or detection date when unavailable.</p>
            <DashboardDisclosure label="Show values in a table">
              <table><caption class="visually-hidden">Open violations by age</caption><thead><tr><th>Age</th><th>Properties</th></tr></thead>
                <tbody><tr v-for="row in agingChart.rows" :key="row.label"><td>{{ row.label }}</td><td>{{ fmtInt(row.value) }}</td></tr></tbody>
              </table>
            </DashboardDisclosure>
          </article>
          <article class="analytics-card">
            <h4>Oldest open violations</h4>
            <p v-if="!oldest.length">No open violations in this snapshot.</p>
            <ul v-else class="worklist">
              <li v-for="property in oldest" :key="property.propertyId">
                <div>
                  <router-link :to="route('violation-detail', { selectedViolationId: property.propertyId })">{{ property.address || 'Address unavailable' }}</router-link>
                  <small>{{ fmtInt(property.activeListingCount) }} active listings · {{ fmtInt(property.letters || 0) }} letters sent</small>
                </div>
                <span>{{ fmtDays(property.openDays) }}</span>
              </li>
            </ul>
            <router-link class="card-link" :to="route('violations')">Manage violations <i class="bi-arrow-right" aria-hidden="true" /></router-link>
          </article>
        </div>
      </section>

      <section aria-label="License revenue estimates">
        <h3>License revenue estimates</h3>
        <p class="fine-print">Annual amounts estimated from this town’s configured fee schedule, not payment records.</p>
        <div v-if="!has('money')" class="analytics-notice">{{ reason('money') }}</div>
        <div v-else class="analytics-grid">
          <article class="analytics-card">
            <h4>{{ snapshot.revenue.canSplitStr ? 'Active STR licenses, annual' : 'Active licenses, annual' }}</h4>
            <div class="stat-value">{{ fmtUsd(snapshot.revenue.canSplitStr ? snapshot.revenue.strAnnual : snapshot.revenue.allAnnual) }}</div>
            <p>All license types: {{ fmtUsd(snapshot.revenue.allAnnual) }}</p>
            <p v-if="snapshot.revenue.unpriceableLicenses" class="fine-print">{{ fmtInt(snapshot.revenue.unpriceableLicenses) }} active licenses could not be priced and are excluded.</p>
            <a v-if="feeSourceUrl" :href="feeSourceUrl" target="_blank" rel="noopener noreferrer">Published fee schedule ↗</a>
          </article>
          <article class="analytics-card">
            <h4>Potential revenue from open violations</h4>
            <template v-if="snapshot.revenue.outstanding?.violationTotal != null">
              <div class="stat-value">{{ fmtUsd(snapshot.revenue.outstanding.violationTotal) }}</div>
              <p>{{ fmtInt(snapshot.revenue.outstanding.violationCount) }} properties at an assumed {{ fmtUsd(snapshot.revenue.outstanding.violationUnitFee) }} each annually.</p>
              <p class="fine-print">Includes deferred violations. Unmatched listings are excluded. This is a scenario estimate, not an amount owed.</p>
            </template>
            <p v-else class="unavailable">No recovery estimate is configured for this municipality.</p>
          </article>
        </div>
      </section>

      <section aria-label="Where the listings are">
        <h3>Where the listings are</h3>
        <article class="analytics-card">
          <div class="map-filters" aria-label="Filter listings on map">
            <button type="button" :aria-pressed="mapFilter === null" @click="mapFilter = null">All</button>
            <button v-for="item in complianceChart.segments" :key="item.key" type="button" :aria-pressed="mapFilter === item.key" @click="mapFilter = mapFilter === item.key ? null : item.key">
              <span :style="{ color: item.color }">{{ categories[item.key]?.glyph }}</span> {{ item.label }} ({{ fmtInt(mapCounts[item.key] || 0) }})
            </button>
          </div>
          <DashboardGraphic kind="map" label="Active listings in this municipality" :options="mapChart" :filter="mapFilter" @select="openListing" />
          <p class="fine-print">
            {{ fmtInt(snapshot.map.points?.length || 0) }} active listings plotted · {{ fmtInt(snapshot.map.missingCoordinates || 0) }} without coordinates<span v-if="snapshot.map.invalidCoordinates"> · {{ fmtInt(snapshot.map.invalidCoordinates) }} with invalid coordinates</span><span v-if="snapshot.map.outsideBoundary"> · {{ fmtInt(snapshot.map.outsideBoundary) }} outside the municipal boundary</span>.
            Select a marker to open the listing in the portal.
          </p>
          <DashboardDisclosure class="map-table" label="Show mapped listings in a table">
            <div class="table-scroll"><table><caption class="visually-hidden">Mapped active listings</caption><thead><tr><th>Listing</th><th>Platform</th><th>Status</th></tr></thead>
              <tbody><tr v-for="point in filteredPoints" :key="point.id">
                <td><router-link :to="route('listing-detail', { selectedListingId: point.id })">{{ point.address || point.title || point.id }}</router-link></td>
                <td>{{ sourceInfo(point.source).name }}</td><td>{{ categories[point.category]?.label || point.category }}</td>
              </tr></tbody>
            </table></div>
          </DashboardDisclosure>
        </article>
        <article v-if="snapshot.inventory.topProperties?.length" class="analytics-card top-properties">
          <h4>Properties with the most active listings</h4>
          <ul class="worklist">
            <li v-for="property in snapshot.inventory.topProperties" :key="property.id">
              <router-link v-if="property.violation" :to="route('violation-detail', { selectedViolationId: property.id })">{{ property.address || 'Address unavailable' }}</router-link>
              <span v-else>{{ property.address || 'Address unavailable' }}</span>
              <span>{{ fmtInt(property.count) }} listings</span>
            </li>
          </ul>
        </article>
      </section>
    </template>
    <DashboardHistory :key="cityId" :city-id="cityId" :compliance-options="complianceOptions" />
    <DashboardTrendsPanel v-if="trendsMetric && showSnapshot" :snapshot="snapshot" :initial-metric="trendsMetric" :compliance-options="complianceOptions" :rows="historyRows" :loading="historyLoading" :error="historyError" @close="trendsMetric = null" @retry="reloadHistory" />
    <slot name="data-quality" />
  </section>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, defineAsyncComponent, onBeforeUnmount, provide, ref, shallowRef, toRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { requestApi } from '@/api';
import DashboardGraphic from './DashboardGraphic.vue';
import DashboardDisclosure from './DashboardDisclosure.vue';
import DashboardHistory from './DashboardHistory.vue';
import DashboardTrend from './DashboardTrend.vue';
import DashboardTrendsPanel from './DashboardTrendsPanel.vue';
import { dashboardHistoryKey, useDashboardHistory } from './useDashboardHistory';
import ComplianceRateCard from './ComplianceRateCard.vue';
import PlatformListingsCard from './PlatformListingsCard.vue';
import { DEFAULT_COMPLIANCE_OPTIONS } from '@strenforcement/domain/dashboardCompliance';
import { dashboardListingPopulation } from '@strenforcement/domain/dashboardListings';
import { getListingSourceInfo } from '@strenforcement/domain/listing';
import { ENABLE_DASHBOARD_ASSISTANT, ENABLE_DASHBOARD_REDESIGN } from '@/featureFlags';
import { fmtInt, fmtUsd, fmtPct, fmtDays, fmtDate } from '../../../../dashboard/public/js/format.js';

const props = defineProps({
  cityId: { type: String, required: true }, cityName: { type: String, default: '' },
  summary: { type: Object, default: null }, summaryLoading: Boolean, summaryError: { type: String, default: null }
});
defineEmits(['reload-summary']);
const DashboardAssistant = defineAsyncComponent(() => import('./DashboardAssistant.vue'));
const router = useRouter();
const currentRoute = useRoute();
const dashboardRedesignEnabled = computed(() => {
  // Keep previews URL-scoped; missing or ambiguous values use the build default.
  const override = currentRoute.query.dashboardRedesign;
  if (override === 'true') return true;
  if (override === 'false') return false;
  return ENABLE_DASHBOARD_REDESIGN;
});
const snapshot = shallowRef(null);
const loading = ref(false);
const error = ref(null);
const unavailable = ref(false);
const showSnapshot = computed(() => Boolean(snapshot.value) && !loading.value && !error.value);
const mapFilter = ref(null);
const complianceOptions = ref({ ...DEFAULT_COMPLIANCE_OPTIONS });
const sharedHistory = useDashboardHistory(toRef(props, 'cityId'));
provide(dashboardHistoryKey, sharedHistory);
const { history, loading: historyLoading, error: historyError } = sharedHistory;
const historyRows = computed(() => history.value?.rows || []);
const reloadHistory = () => sharedHistory.load(true);
const trendsMetric = ref(null);
function openTrends(metric) {
  trendsMetric.value = metric;
  sharedHistory.load();
}
let controller;
let generation = 0;
const categories = {
  licensed: { label: 'Licensed', color: '#2a9d5c', glyph: '●' },
  violation: { label: 'In violation', color: '#d44b4b', glyph: '▲' },
  needsAttention: { label: 'Unverified', color: '#d4870b', glyph: '◆' },
  deferred: { label: 'Deferred', color: '#9e9e9e', glyph: '○' }
};
const reasons = {
  'no-fee-schedule': 'No fee schedule has been configured for this municipality, so revenue cannot be estimated.',
  'no-licences': 'This municipality has no license records in the system.',
  'no-census-denominator': 'A Census housing-unit count is not available for this municipality.',
  'no-property-records': 'This municipality has no parcel records available to calculate property-based metrics.'
};
const has = key => snapshot.value?.capabilities?.[key]?.available === true && (key !== 'money' || Boolean(snapshot.value?.revenue));
const reason = key => reasons[snapshot.value?.capabilities?.[key]?.reason] || 'This measure is not available in the current snapshot.';
const route = (name, params = {}, query = {}) => ({ name, params: { cityId: props.cityId, ...params }, query });
const displayPercent = value => value == null ? '—' : fmtPct(value);
const listingPopulation = computed(() => dashboardListingPopulation(snapshot.value));
const verifiedShare = computed(() => listingPopulation.value?.rate ?? null);
const showListingBracket = computed(() => listingPopulation.value?.deferred > 0 && listingPopulation.value.included > 0);
const listingPopulationLabel = computed(() => `Active listings: verified, unverified, and deferred.${showListingBracket.value ? ' The bracket marks non-deferred listings included in the verification rate.' : ''}`);
const verificationChart = computed(() => {
  if (!listingPopulation.value) return null;
  return { valueLabel: 'listings', totalLabel: 'of non-deferred active listings', height: 164,
    showCenter: false, innerRadiusRatio: 0.78, emptyColor: '#e8e6e3', segments: [
    { key: 'verified', label: 'Verified', value: listingPopulation.value.verified, color: '#3b7dd8' },
    { key: 'unverified', label: 'Unverified', value: listingPopulation.value.unverified, color: categories.needsAttention.color }
  ].filter(item => item.value > 0) };
});
const listingPopulationChart = computed(() => {
  if (!verificationChart.value) return null;
  return { valueLabel: 'listings', totalLabel: 'of active listings', height: 16, showValues: false,
    bracketKeys: showListingBracket.value ? ['verified', 'unverified'] : [], segments: [
      ...verificationChart.value.segments,
      { key: 'deferred', label: 'Deferred', value: listingPopulation.value.deferred, color: categories.deferred.color }
    ].filter(item => item.value > 0) };
});
const classicListingChart = computed(() => listingPopulationChart.value
  ? { ...listingPopulationChart.value, height: 46, showValues: true } : null);
const listingDiscoveryHistory = computed(() => {
  const flow = snapshot.value?.temporal?.flow;
  if (!Array.isArray(flow)) return [];
  const periods = flow.slice(-4).map(period => ({
    key: period.key, label: period.label, partial: period.partial === true,
    count: Number.isFinite(period.discovered) && period.discovered >= 0 ? period.discovered : null
  }));
  if (!periods.some(period => period.count != null)) return [];
  const max = Math.max(1, ...periods.map(period => period.count ?? 0));
  return periods.map(period => ({ ...period, height: (period.count ?? 0) / max * 100 }));
});
const licenseStats = computed(() => {
  const licenses = props.summary?.licenses || {};
  const firstExpiryDate = Object.keys(licenses.expiresBy || {}).sort()[0];
  return [
    { label: 'Active licenses', value: licenses.active, query: { onlyShowActive: true } },
    { label: 'Pending', value: licenses.pending, query: { onlyShowPending: true } },
    { label: 'Expired', value: licenses.expired },
    { label: 'Expiring soon', value: firstExpiryDate ? licenses.expiresBy[firstExpiryDate] : null }
  ];
});
const workflowColors = { issued: '#2a9d5c', submitted: '#d4870b', suspended: '#c46d31', expired: '#8a8a8a', closed: '#546875', withdrawn: '#a2687d', deleted: '#a5a5a5' };
const customWorkflowColors = ['#3b7dd8', '#8870b5', '#268c91', '#b37335', '#aa6085', '#607f45'];
const licenseWorkflowChart = computed(() => {
  const workflow = snapshot.value?.licenseWorkflow;
  if (workflow?.definition !== 'one-relevant-license-per-property-v1') return null;
  const statuses = workflow.byWorkflowStatus;
  if (!Array.isArray(statuses) || !statuses.every(item => Number.isSafeInteger(item.count) && item.count >= 0)
    || statuses.reduce((sum, item) => sum + item.count, 0) !== workflow.totalProperties) return null;
  let customIndex = 0;
  const segments = statuses.map(item => ({
    key: item.code ?? '__unknown__', label: item.label, value: item.count,
    color: item.code == null ? '#767676' : workflowColors[item.code] || customWorkflowColors[customIndex++ % customWorkflowColors.length]
  })).filter(item => Number.isFinite(item.value) && item.value > 0);
  return { segments, valueLabel: 'properties', totalLabel: 'of properties with licenses', centerLabel: 'properties' };
});
const licenseWorkflowTotal = computed(() => licenseWorkflowChart.value?.segments.reduce((sum, item) => sum + item.value, 0) || 0);
function sourceInfo(source) {
  const key = String(source || '').toLowerCase();
  return getListingSourceInfo(key === 'booking.com' ? 'booking' : key);
}
const openListing = point => router.push(route('listing-detail', { selectedListingId: point.id }));
const stale = computed(() => Date.now() - Date.parse(snapshot.value?.generatedAt) > 4 * 86400000);
const oldest = computed(() => snapshot.value?.violations.openProperties?.slice(0, 10) || []);
const complianceChart = computed(() => ({ segments: (snapshot.value?.compliance.categories || []).map(item => ({
  key: item.key, label: categories[item.key]?.label || item.label, value: item.count,
  color: categories[item.key]?.color || '#6b7280'
})) }));
const agingChart = computed(() => ({ rows: (snapshot.value?.violations.aging || []).map(item => ({
  label: item.label, value: item.count, valueLabel: 'properties'
})), color: categories.violation.color, labelWidth: 95, barHeight: 18 }));
const mapChart = computed(() => ({
  boundary: snapshot.value?.map.boundary,
  points: snapshot.value?.map.points || [],
  categories,
  mapsKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY || null
}));
const mapCounts = computed(() => mapChart.value.points.reduce((counts, point) => {
  counts[point.category] = (counts[point.category] || 0) + 1;
  return counts;
}, {}));
const filteredPoints = computed(() => mapChart.value.points.filter(point => !mapFilter.value || point.category === mapFilter.value));
const feeSourceUrl = computed(() => {
  const url = snapshot.value?.revenue?.feeScheduleSource?.url;
  return /^https?:\/\//i.test(url || '') ? url : null;
});

async function load(refreshHistory = false) {
  const current = ++generation;
  controller?.abort();
  controller = new AbortController();
  snapshot.value = null;
  error.value = null;
  unavailable.value = false;
  mapFilter.value = null;
  loading.value = true;
  try {
    const data = await requestApi(`/v1/cities/${encodeURIComponent(props.cityId)}/dashboard`, {
      requireAuth: true, signal: controller.signal
    });
    if (current !== generation) return;
    if (data.city?.id !== props.cityId) throw new Error('Wrong municipality');
    snapshot.value = data;
    sharedHistory.load(refreshHistory === true);
  } catch (err) {
    if (current !== generation) return;
    unavailable.value = err.status === 404;
    error.value = unavailable.value ? 'Analytics are not available for this municipality yet.'
      : err.status === 403 ? 'You do not have access to this municipality’s analytics.'
      : 'Town analytics could not be loaded. Please try reloading the snapshot.';
  } finally {
    if (current === generation) loading.value = false;
  }
}
watch(() => props.cityId, () => {
  trendsMetric.value = null;
  complianceOptions.value = { ...DEFAULT_COMPLIANCE_OPTIONS };
  load();
}, { immediate: true });
onBeforeUnmount(() => { ++generation; controller?.abort(); });
</script>

<style scoped>
.city-analytics {
  --surface: #fff; --text-primary: #1a1a1a; --text-secondary: #6b6b6b; --text-tertiary: #767676;
  --border: #e8e6e3; --border-hover: #d0cec9; --green: #2a9d5c; --green-bg: #eefbf3;
  --amber: #b26d00; --amber-bg: #fff8ec; --red: #d44b4b; --red-bg: #fff0f0;
  --blue: #3b7dd8; --blue-bg: #eef4fc; --radius: 12px; --radius-sm: 8px;
  --shadow-sm: 0 1px 2px rgba(0,0,0,.04);
  width: 100%; padding-bottom: 20px; color: var(--text-primary);
  container: dashboard / inline-size;
}
.analytics-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
h1 { font-size: 22px; font-weight: 600; letter-spacing: -.02em; margin: 0 0 6px; }
h2, h3 { font-size: 16px; font-weight: 600; margin: 28px 0 12px; }
h4 { font-size: 14px; font-weight: 600; margin: 0 0 14px; }
p { font-size: 13px; color: var(--text-secondary); margin: 0 0 12px; }
.snapshot-note { margin: 0 0 20px; font-size: 12px; }
.snapshot-note strong { display: block; color: #985600; }
.analytics-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.analytics-card, .analytics-notice { padding: 20px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); min-width: 0; box-shadow: var(--shadow-sm); }
.analytics-notice { font-size: 13px; color: var(--text-secondary); }
.compliance-card, .active-listings-card { display: flex; flex-direction: column; }
.active-listings-card { container: listings-card / inline-size; }
.card-heading { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 14px; }
.card-heading .card-label { margin: 0; }
.view-trends { display: inline-flex; align-items: center; justify-content: center; border: 1px solid var(--border); border-radius: 6px; width: 30px; height: 30px; padding: 0; background: transparent; color: var(--text-secondary); font-size: 13px; }
.view-trends:hover { border-color: var(--border-hover); color: var(--text-primary); }
.view-trends:focus-visible { outline: 2px solid var(--blue); outline-offset: 2px; }
@media (pointer: coarse) { .view-trends { width: 44px; height: 44px; } }
.classic-card-content { display: grid; grid-template-columns: minmax(0, 1fr); gap: 20px; }
.classic-card-content > div { min-width: 0; }
.classic-card-summary { display: grid; align-content: start; gap: 14px; }
.classic-card-summary :deep(.dashboard-trend) { width: 100%; max-width: 220px; margin: 0; text-align: left; }
.classic-card-summary .stat-value { font-size: 36px; line-height: 1.1; margin: 0; min-width: 0; overflow-wrap: anywhere; }
.verification-heading { margin: 0 0 10px; font-size: 12px; line-height: 1.5; text-align: left; }
.card-actions { display: flex; flex-wrap: wrap; gap: 4px 12px; margin-top: auto; padding-top: 16px; }
.card-actions a, .card-link, .section-heading a, .management-links a { display: inline-flex; align-items: center; gap: 7px; padding: 7px 10px; border: 1px solid var(--border); border-radius: 6px; color: var(--text-secondary); background: transparent; font-size: 12px; text-decoration: none; }
.card-actions a:hover, .card-link:hover, .section-heading a:hover, .management-links a:hover { border-color: var(--border-hover); color: var(--text-primary); background: #f5f5f3; }
.card-actions a:focus-visible, .card-link:focus-visible, .section-heading a:focus-visible, .management-links a:focus-visible { outline: 2px solid var(--text-secondary); outline-offset: 2px; }
.card-label { font-size: 13px; font-weight: 500; line-height: 30px; color: var(--text-secondary); margin: 0 0 14px; }
.listing-verification .donut-breakdown { display: flex; flex-wrap: wrap; align-items: center; gap: 16px 20px; }
.verification-donut { position: relative; flex: 0 0 164px; width: 164px; margin: 0 auto; }
.verification-donut .stat-value { position: absolute; inset: 0; display: grid; place-items: center; margin: 0; pointer-events: none; }
.listing-population { border-top: 1px solid var(--border); margin-top: 22px; padding-top: 16px; }
.population-heading { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 4px 12px; font-size: 12px; margin-bottom: 10px; }
.population-heading > span { color: var(--text-secondary); }
.population-heading strong, .population-labels strong { font-weight: 500; font-variant-numeric: tabular-nums; }
.population-labels { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px 16px; font-size: 12px; margin-top: 5px; }
.population-labels span { display: block; color: var(--text-secondary); }
.deferred-context { margin-left: auto; text-align: right; }
.stat-value { font-family: var(--bs-font-monospace); font-size: 32px; font-weight: 600; letter-spacing: -.04em; font-variant-numeric: tabular-nums; line-height: 1.2; margin: 0 0 12px; }
.stat-caption { font-size: 13px; line-height: inherit; margin: 0 0 12px; }
.stat-caption strong, .verification-heading strong { color: var(--text-primary); font-weight: 600; }
.donut-breakdown { display: flex; flex-wrap: wrap; align-items: center; gap: 12px 20px; }
.donut-breakdown > .legend { flex: 1 1 130px; min-width: 0; margin: 0; }
.verification-summary { flex: 1 1 120px; min-width: 0; }
.verification-summary .legend { margin: 0 0 12px; }
.verification-summary .fine-print { margin: 0; }
.verification-definition { margin: 18px 0 0; }
.listing-discovery { border-top: 1px solid var(--border); margin-top: 22px; padding-top: 16px; }
.listing-discovery h4 { font-size: 12px; font-weight: 500; color: var(--text-secondary); margin-bottom: 12px; }
.listing-discovery .fine-print { margin-bottom: 0; }
.discovery-history { display: flex; list-style: none; padding: 0; margin: 0 0 10px; }
.discovery-history li { flex: 1; min-width: 0; text-align: center; font-size: 10px; color: var(--text-secondary); }
.discovery-history strong { display: block; font-size: 12px; font-weight: 500; font-variant-numeric: tabular-nums; color: var(--text-primary); margin-bottom: 5px; }
.discovery-track { display: flex; align-items: flex-end; justify-content: center; height: 44px; border-bottom: 1px solid var(--border); }
.discovery-bar { width: 24px; max-width: 65%; background: #aab9ce; border-radius: 3px 3px 0 0; }
.partial .discovery-bar { background: repeating-linear-gradient(135deg, #aab9ce, #aab9ce 3px, #e5eaf1 3px, #e5eaf1 6px); }
.discovery-period { display: block; margin-top: 6px; }
.discovery-partial { display: block; min-height: 15px; }
.reload-button { border: 1px solid var(--border); border-radius: var(--radius-sm); background: var(--surface); padding: 7px 12px; font-size: 12px; color: var(--text-secondary); white-space: nowrap; }
.reload-button:hover { border-color: var(--border-hover); color: var(--text-primary); }
.reload-button:disabled { opacity: .6; }
.section-heading { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; }
.license-activity { grid-column: 1 / -1; container: license-activity / inline-size; }
.license-activity .section-heading { flex-wrap: wrap; gap: 4px 16px; margin-bottom: 12px; }
.license-activity .section-heading h2 { font-size: 13px; font-weight: 500; color: var(--text-secondary); margin: 0; }
.license-stats { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface); overflow: hidden; }
.license-stats a { display: flex; flex-direction: column; gap: 6px; padding: 20px; text-decoration: none; color: var(--text-primary); border-right: 1px solid var(--border); }
.license-stats a:nth-child(2n) { border-right: 0; }
.license-stats a:nth-child(-n+2) { border-bottom: 1px solid var(--border); }
.license-stats a:hover { background: #f5f5f3; }
.license-stats span { font-size: 12px; color: var(--text-secondary); }
.license-stats strong { font: 600 28px var(--bs-font-monospace); letter-spacing: -.03em; }
.license-workflow { margin-top: 24px; }
.license-workflow .section-heading h2 { margin-top: 0; }
.license-workflow-breakdown { display: grid; grid-template-columns: minmax(160px, 220px) minmax(0, 1fr); align-items: center; gap: 28px; }
.workflow-legend { max-width: 440px; }
.workflow-legend li > span:nth-child(2) { overflow-wrap: anywhere; }
.workflow-legend strong { font-family: var(--bs-font-monospace); }
.workflow-note { margin: 12px 0 0; }
.management-links { display: flex; flex-wrap: wrap; gap: 8px; font-size: 13px; }
.fine-print { font-size: 12px; line-height: 1.5; }
.unavailable { margin: 0; }
.legend { list-style: none; padding: 0; margin: 16px 0; display: grid; gap: 8px; font-size: 12px; }
.legend li { display: flex; align-items: center; gap: 6px; }
.legend strong { margin-left: auto; }
.legend-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.listing-counts { gap: 0; margin-bottom: 0; }
.listing-counts li { padding: 10px 0; border-bottom: 1px solid var(--border); }
.listing-counts li:last-child { border-bottom: 0; }
.worklist { list-style: none; margin: 0 0 16px; padding: 0; max-height: 300px; overflow: auto; }
.worklist li { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; border-bottom: 1px solid #eee; padding: 10px 0; font-size: 13px; }
.worklist small { display: block; color: #626970; font-size: 11px; margin-top: 4px; }
.worklist li > span:last-child { white-space: nowrap; font-variant-numeric: tabular-nums; }
.worklist a, .worklist li > div { overflow-wrap: anywhere; min-width: 0; }
.analytics-card > a { font-size: 13px; }
.map-filters { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.map-filters button { border: 1px solid var(--border); background: white; padding: 6px 10px; border-radius: var(--radius-sm); font-size: 12px; }
.map-filters button[aria-pressed='true'] { background: #f5f5f3; border-color: var(--text-secondary); }
.top-properties { margin-top: 16px; }
table { width: 100%; text-align: left; font-size: 12px; }
th, td { padding: 7px 5px; border-bottom: 1px solid #eee; }
.table-scroll { max-height: 350px; overflow: auto; }
/* Size the current layout by its available content width, not the viewport. */
@container listings-card (min-width: 420px) {
  .classic-card-content { grid-template-columns: minmax(140px, .8fr) minmax(0, 1.3fr); gap: 20px; }
}
@container dashboard (min-width: 1100px) {
  .city-analytics:not(.dashboard-redesign) .overview-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .city-analytics:not(.dashboard-redesign) .compliance-card,
  .city-analytics:not(.dashboard-redesign) .active-listings-card,
  .city-analytics:not(.dashboard-redesign) .license-activity { grid-column: span 2; }
  .city-analytics:not(.dashboard-redesign) .overview-summary-only > .license-activity { grid-column: 1 / -1; }
  .overview-grid .card-label { line-height: 1.5; }
  .active-listings-card > .card-label { min-height: 30px; }
}
@container dashboard (max-width: 680px) {
  .analytics-grid { grid-template-columns: minmax(0, 1fr); }
  .analytics-heading { flex-direction: column; }
}
@container license-activity (min-width: 640px) {
  .license-stats { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .license-stats a:nth-child(n) { border-bottom: 0; border-right: 1px solid var(--border); }
  .license-stats a:last-child { border-right: 0; }
}
@media (max-width: 540px) {
  .license-workflow-breakdown { grid-template-columns: minmax(0, 1fr); gap: 8px; }
  .license-workflow-breakdown > .dashboard-graphic { width: 220px; max-width: 100%; margin: 0 auto; }
  .section-heading { flex-wrap: wrap; gap: 0; margin-bottom: 10px; }
}
</style>
